import { AfterViewChecked, ChangeDetectorRef, Component, OnDestroy, OnInit, effect } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';

import { TranslateModule } from '@ngx-translate/core';
import { CardSubmitRequest, CardSubmitUseCase, EventType } from '@payments/ngpgcard-utils';
import { Subscription } from 'rxjs';

import { AppConstants, InputType } from '../../../config/appConstants';
import { DepositInfo } from '../../../model/depositInfo.model';
import { QDInstrument } from '../../../model/qdInstrument.model';
import { ApplePayDirectService } from '../../../service/apple-pay-direct-service.service';
import { BehaviorDataService } from '../../../service/behaviorData.service';
import { BetslipQdMessageService } from '../../../service/betslip-qd-message.service';
import { CardIframeService } from '../../../service/card-iframe-service.service';
import { CcDepositSubmitService } from '../../../service/ccDepositSubmit.service';
import { DataService } from '../../../service/data.service';
import { DepositService } from '../../../service/deposit.service';
import { LoaderService } from '../../../service/loader.service';
import { OptionListService } from '../../../service/optionList.service';
import { StakeAmountService } from '../../../service/stakeAmount.service';
import { UserDataService } from '../../../service/userDataService';
import { WorldPayFetchSessionIdService } from '../../../service/worldPayFetchSessionId.service';
import { AdditionalMessagesComponent } from '../common/additional-messages/additional-messages.component';
import { DepositCurfewMessageComponent } from '../common/additional-messages/deposit-curfew-message/deposit-curfew-message.component';
import { RedirectMessageComponent } from '../common/additional-messages/redirect-message/redirect-message.component';
import { DepositFailureComponent } from '../common/deposit-failure/deposit-failure.component';
import { DepositSuccessComponent } from '../common/deposit-success/deposit-success.component';
import { OptionsListComponent } from '../common/options-list/options-list.component';
import { StakeMessageComponent } from '../common/stake-message/stake-message.component';
import { LCGTxnDetailsComponent } from './txn-details/txn-details.component';

@Component({
    selector: 'app-lcg-betslip',
    templateUrl: 'lcg-betslip.component.html',
    imports: [
        DepositSuccessComponent,
        DepositFailureComponent,
        AdditionalMessagesComponent,
        StakeMessageComponent,
        FormsModule,
        ReactiveFormsModule,
        OptionsListComponent,
        RedirectMessageComponent,
        LCGTxnDetailsComponent,
        TranslateModule,
        DepositCurfewMessageComponent,
    ],
})
export class LCGBetslipComponent implements OnInit, AfterViewChecked, OnDestroy {
    depositForm: FormGroup;

    isVisible = false;
    kycPLverification: boolean;
    inputType: string;
    ccFamily = AppConstants.CCFAMILY;
    depositInfo: DepositInfo;
    selectedOption: QDInstrument;
    worldPaySessionIdStatus: boolean;
    isBetSuspended: boolean;
    isPriceChanged: boolean;
    activeDepositCurfew: boolean;
    subscriptions$: Subscription[] = [];
    isPaypalBadFunding: boolean;
    sessionTimeUpdateUrl: any;
    paypaldirect_collectedDeviceData: any;
    constructor(
        public depositService: DepositService,
        public loaderService: LoaderService,
        private userDataService: UserDataService,
        private dataService: DataService,
        private ccDepositService: CcDepositSubmitService,
        private behaviorData: BehaviorDataService,
        public optionListService: OptionListService,
        private changeDetector: ChangeDetectorRef,
        public stakeAmountService: StakeAmountService,
        private wpSessionService: WorldPayFetchSessionIdService,
        private formBuilder: FormBuilder,
        private applePayDirectService: ApplePayDirectService,
        private cardIframeService: CardIframeService,
        private betslipqdMessageService: BetslipQdMessageService,
    ) {
        effect(() => {
            const inputMessage = this.betslipqdMessageService.input();
            if (inputMessage?.eventName) {
                this.handleParentMessages({ data: inputMessage } as MessageEvent);
            }
        });
    }

    ngOnInit() {
        this.buildForm();
        this.depositService.showDepositInput.set(true);
        if (!this.optionListService?.isDataLoading()) {
            this.initialize();
            this.getBehaviorData();
        }
    }

    initialize() {
        this.setDepositOptionInfo();
    }
    updatePaymentMethod(payName: string) {
        this.inputType = payName;
    }

    get instrumentFormGroup(): FormGroup {
        return this.depositForm.controls['instrumentFormGroup'] as FormGroup;
    }

    setDepositOptionInfo() {
        this.selectedOption = this.optionListService?.selectedOption;
        this.depositService.depositInfo.amountInfo.nativeCurrency =
            this.selectedOption.currencyInfo.defaultCurr || this.selectedOption.currencyInfo.currencyList[0];

        const threeds2Enabled = this.selectedOption?.threeds2Enabled;
        if (threeds2Enabled && this.userDataService?.userData?.configParams?.worldPayTokenForDDC) this.getSessionIdForWP();
        this.isVisible = true;
    }

    getActiveDepositCurfew(data: any) {
        this.activeDepositCurfew = data;
    }
    getSessionIdForWP() {
        const worldpayTimerDuration = this.userDataService?.userData?.configParams?.worldpayTimerDuration
            ? this.userDataService?.userData?.configParams?.worldpayTimerDuration * 1000
            : 5000;
        this.wpSessionService.fetchWPSessionId(
            this.userDataService?.userData?.configParams?.baseWorldPayURLForDDC,
            this.userDataService?.userData?.configParams?.worldPayTokenForDDC,
            this.userDataService?.userData?.configParams?.worldPayURLForDDC,
            this.selectedOption?.startDigits,
        );

        setTimeout(() => {
            if (!this.worldPaySessionIdStatus) {
                this.setWorldPaySessionId('', {
                    reason: 'wp_timeout',
                    info: 'not_received_sessionId_within_duration',
                });
            }
        }, worldpayTimerDuration); //worldpayTimerDuration is a config driven , default is 5000
    }

    setWorldPaySessionId(worldPaySessionId: any, data: any) {
        this.depositService.depositInfo.worldPaySessionId = worldPaySessionId;
        if (data?.reason == 'worldPayResponse') this.depositService.depositInfo.paymentAccountInfo.worldPayResponse = JSON.stringify(data.info);
        else if (data?.reason == 'wp_timeout') this.depositService.depositInfo.paymentAccountInfo.wpTimeout = data.info;
        this.worldPaySessionIdStatus = true;
    }

    handleParentMessages(event: any) {
        this.stakeAmountService.addEventListener(event);
    }

    submitDeposit() {
        if (this.depositService.depositInfo.paymentMethod === InputType.PAYPALDIRECT) {
            // TODO: paypaldirect_collectedDeviceData always undefined... recheck it
            if (this.depositService.depositInfo.paymentAccountInfo) {
                this.depositService.depositInfo.paymentAccountInfo.deviceData = this.paypaldirect_collectedDeviceData;
            } else {
                const paymentAccountInfo = {
                    deviceData: this.paypaldirect_collectedDeviceData,
                };
                this.depositService.depositInfo.paymentAccountInfo = paymentAccountInfo;
            }
        }
        this.depositService.showDepositFailure = false;
        this.depositService.resizeCashier();
        let proceedToSubmit = true;
        const threeds2Enabled = this.optionListService?.selectedOption?.threeds2Enabled;
        if (this.userDataService?.userData?.configParams?.worldPayTokenForDDC) {
            proceedToSubmit = false;
        }

        if (this.activeDepositCurfew) return false;

        if (threeds2Enabled) {
            this.wpSessionService.fetchWPSessionId(
                this.userDataService?.userData?.configParams?.baseWorldPayURLForDDC,
                this.userDataService?.userData?.configParams?.worldPayTokenForDDC,
                this.userDataService?.userData?.configParams?.worldPayURLForDDC,
                this.selectedOption?.startDigits,
            );
        }

        if (!threeds2Enabled || proceedToSubmit) {
            if (this.optionListService?.selectedOption?.paymentMethodInfo?.isNonPCIOption === 'true') {
                this.submitCCDeposit();
            } else {
                this.depositService.submitDeposit();
            }
        } else {
            this.depositService.depositInfo.worldPaySessionId = this.dataService?.worldPaySessionId;
            this.submitCCDeposit();
        }
        return false;
    }

    private submitCCDeposit() {
        // NGPG  Submit  Call
        if (this.userDataService?.userData?.configParams?.qd_ngpg_iframe_enabled === 'Y') {
            //CardSubmitRequest
            const request: CardSubmitRequest = {
                submitUrl: this.optionListService?.selectedOption?.paymentAccountInfo?.paymentAccountUpdateUrl,
                jwtToken: this.optionListService?.selectedOption?.paymentAccountInfo?.cardToken ?? '',
                usecase: CardSubmitUseCase.update_cvv,
            };
            this.cardIframeService._eventBridge()?.off(EventType.card_submit_response);
            this.cardIframeService._eventBridge()?.sendMessageToIframe({ eventName: EventType.card_submit, payload: request });
            this.cardIframeService._eventBridge()?.receiveEvent(EventType.card_submit_response, this.handleIframeResponse.bind(this));
        } else {
            this.ccDepositService.submitCCDeposit(
                this.depositService?.depositInfo?.paymentAccountInfo?.cvv2,
                this.optionListService?.selectedOption?.paymentAccountInfo?.paymentAccountUpdateUrl,
                this.depositService.handleResponse,
                this.optionListService?.selectedOption?.paymentAccountInfo?.cardToken,
            );
        }
    }
    private handleIframeResponse(data: any) {
        if (!data?.errorcode) {
            this.depositService.submitDeposit();
        }
    }

    getBehaviorData() {
        this.kycPLverification = ['SUSPENDED_GRACE_DAYS_OVER', 'SUSPENDED_DEPOSIT_AMOUNT_EXCEEDED'].includes(
            this.userDataService?.userData?.userInfo?.ezecashCategoryComment,
        );

        this.subscriptions$.push(
            this.behaviorData?.getPaypalBadFunding.subscribe((data) => {
                this.isPaypalBadFunding = data;
            }),
        );
        this.subscriptions$.push(
            this.behaviorData?.isBetSuspended.subscribe((data) => {
                this.isBetSuspended = data;
            }),
        );
        this.subscriptions$.push(
            this.behaviorData.isPriceChanged.subscribe((data) => {
                this.isPriceChanged = data;
            }),
        );
    }

    buildForm() {
        this.depositForm = this.formBuilder.group({
            instrumentFormGroup: new FormGroup({}),
            ccInputFormGroup: new FormGroup({}),
        });
    }

    ngAfterViewChecked() {
        this.changeDetector.detectChanges();
    }

    ngOnDestroy() {
        this.subscriptions$.map((subscription) => subscription.unsubscribe());
    }
    applePayDepositSubmit() {
        const applePayData = {
            paymentMethod: this.depositService.depositInfo.paymentMethod?.toUpperCase(),
            amountInfo: {
                nativeCurrency: this.depositService?.depositInfo?.amountInfo?.nativeCurrency,
                nativeAmountString: this.depositService?.depositInfo?.amountInfo?.nativeAmountString,
            },
            bonusInfo: {
                bonusId: null,
                tncFromBonusService: null,
                bonusTnCAccepted: null,
                bonusCode: '',
            },
            browserInfo: {
                browserLanguage: 'en',
                browserScreenHeight: 1040,
                browserScreenWidth: 1920,
                browserTimeZone: -330,
            },
            germanAMLChangesEnabled: false,
            paymentAccountInfo: {
                paymethodID: '',
                paymentAccountId: '',
                cardType: '',
                originalCardEndDigits: '',
            },
            productPreference: 'SPORTSBOOK',
            worldPaySessionIdStatus: false,
            pageName: this.depositService.depositInfo.paymentMethod?.toUpperCase() + '_input',
        };
        let creditCardBanEnabled = 'N';
        const country = this.userDataService?.userData?.userInfo?.addressInfo?.country;

        if (this.depositService.depositInfo.paymentMethod?.toUpperCase() == 'APPLEPAYDIRECT') {
            creditCardBanEnabled =
                this.optionListService?.selectedOption?.paymentAccountInfo?.isESIntensivePlayer == 'Y' ||
                this.userDataService?.userData?.configParams?.creditCardBanEnabled == 'Y'
                    ? 'Y'
                    : 'N';
            this.applePayDirectService.processApplePayDeposit(
                applePayData,
                creditCardBanEnabled,
                country,
                this.optionListService?.selectedOption?.paymentAccountInfo?.paymentAccountUpdateUrl,
                this.optionListService?.selectedOption?.paymentAccountInfo?.cardToken,
            );
        }
    }
}
